import classNames from 'classnames'
import Foco from 'react-foco'
import { createPortal } from 'react-dom'
import dynamic from 'next/dynamic'
import useHasMounted from '@/hooks/useHasMounted'
import useLockHTMLScroll from '@/hooks/useLockHTMLScroll'
import { CLOSE_SYMBOL } from '@/root/constants'
import type { ControlPosition } from 'react-draggable'

const Draggable = dynamic(() => import('react-draggable'), { ssr: false })
export interface ModalProps {
  children?: JSX.Element
  className?: string
  contentClassName?: string
  closeUIClassName?: string
  modalWrapperClass?: string
  showModal: boolean
  onClose?: () => void
  bodyClassName?: string
  fullScreenOnMobile?: boolean
  childrenClassName?: string
  backgroundClassName?: string
  disableClickOutside?: boolean
  isDraggable?: boolean
  draggableDefaultPosition?: ControlPosition
}

export default function Modal({
  children,
  className,
  modalWrapperClass = '',
  contentClassName,
  closeUIClassName,
  showModal = false,
  onClose,
  bodyClassName = '',
  fullScreenOnMobile = false,
  childrenClassName = '',
  disableClickOutside = false,
  backgroundClassName,
  isDraggable = false,
  draggableDefaultPosition = { x: 0, y: 200 },
}: ModalProps) {
  const hasMounted = useHasMounted()

  useLockHTMLScroll({
    stateOnWhichLockToggles: showModal,
  })

  if (hasMounted) {
    const jsx = (
      <div
        className={classNames('relative mx-auto w-full px-2', {
          [bodyClassName]: bodyClassName,
          'max-w-sm': !bodyClassName,
          'my-6': !fullScreenOnMobile,
          'md:my-6': fullScreenOnMobile,
        })}
      >
        {/*content*/}
        <Foco
          onClickOutside={() => {
            if (onClose && showModal && !disableClickOutside) {
              onClose()
            }
          }}
        >
          <div
            className={
              contentClassName ??
              classNames(
                'bg-ind-white relative flex h-full w-full flex-col border-0 shadow-lg outline-none focus:outline-none',
                {
                  'rounded-t-xl md:rounded-xl': fullScreenOnMobile,
                  'rounded-2xl': !fullScreenOnMobile,
                },
              )
            }
          >
            {/*body*/}
            {onClose && (
              <div
                className={classNames(
                  `absolute -top-9 right-0 z-50 flex cursor-pointer`,
                  {
                    [closeUIClassName ?? '']: closeUIClassName,
                  },
                )}
                onClick={onClose}
              >
                <div
                  className={classNames(
                    'flex h-8 w-8 items-center justify-center rounded-full  text-center',
                    {
                      'bg-grey-bg': !closeUIClassName,
                    },
                  )}
                >
                  {CLOSE_SYMBOL}
                </div>
              </div>
            )}

            <div
              className={classNames(
                'relative flex-auto p-3',
                childrenClassName,
              )}
            >
              {children}
            </div>
          </div>
        </Foco>
      </div>
    )

    return createPortal(
      <div
        id="modal"
        className={`${showModal ? 'block' : 'hidden'} ${className ?? ''} `}
      >
        <div
          className={classNames(
            'no-scrollbar fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden outline-none focus:outline-none',
            {
              [modalWrapperClass]: modalWrapperClass,
              'pointer-events-none': isDraggable,
            },
          )}
        >
          {isDraggable ? (
            <Draggable
              handle=".handleDrag"
              bounds="parent"
              defaultPosition={draggableDefaultPosition}
              defaultClassName="pointer-events-auto"
            >
              {jsx}
            </Draggable>
          ) : (
            jsx
          )}
        </div>
        {!isDraggable && (
          <div
            className={`${backgroundClassName} bg-ind-black fixed inset-0 z-40 opacity-25`}
          />
        )}
      </div>,
      document.getElementById('modal-root') as HTMLElement,
    )
  }

  return null
}
